/*!
 * Shared helpers to hide free / zero order totals in theme views (order history, order details).
 */
import * as React from 'react';

/** Flatten visible text from React nodes (including nested labels / Commerce wrappers) for free/zero detection. */
export const getNodeText = (node: React.ReactNode): string => {
    if (node === null || node === undefined || typeof node === 'boolean') {
        return '';
    }
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node).trim();
    }
    if (Array.isArray(node)) {
        return node
            .map(getNodeText)
            .filter(t => t.length > 0)
            .join(' ')
            .trim();
    }
    if (React.isValidElement(node)) {
        const p = node.props as Record<string, unknown>;
        const fromProps: string[] = [];
        for (const key of ['value', 'label', 'title', 'aria-label', 'ariaLabel']) {
            const v = p[key];
            if (typeof v === 'string' || typeof v === 'number') {
                fromProps.push(String(v).trim());
            }
        }
        const childText = getNodeText(p.children as React.ReactNode);
        return [...fromProps, childText]
            .filter(t => t.length > 0)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
    }
    return '';
};

/** True when a numeric order/cart amount is zero (or non-finite). */
export const isZeroOrFreeAmount = (amount: number | undefined | null): boolean => {
    if (amount === undefined || amount === null) {
        return false;
    }
    return !Number.isFinite(amount) || amount === 0;
};

const getContextualPriceFromPriceNode = (node: React.ReactNode): number | undefined => {
    if (!React.isValidElement(node)) {
        return undefined;
    }
    const props = node.props as Record<string, unknown>;
    const data = props.data as { price?: Record<string, unknown> } | undefined;
    const price = data?.price;
    if (!price || typeof price !== 'object') {
        return undefined;
    }
    const candidates = [price.CustomerContextualPrice, price.AdjustedPrice, price.BasePrice];
    for (const candidate of candidates) {
        const asNumber = Number(candidate);
        if (Number.isFinite(asNumber)) {
            return asNumber;
        }
    }
    return undefined;
};

/** True when the rendered amount should be hidden (free label, zero, etc.). */
export const isFreeLikeNode = (node: React.ReactNode): boolean => {
    const numericPrice = getContextualPriceFromPriceNode(node);
    if (numericPrice !== undefined && isZeroOrFreeAmount(numericPrice)) {
        return true;
    }

    const text = getNodeText(node).toLowerCase();
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) {
        return false;
    }

    if (normalized.includes('free')) {
        return true;
    }

    if (/\$?\s*0(?:\.0+)?\b/.test(normalized)) {
        return true;
    }

    return (
        normalized === '$0' ||
        normalized === '$0.0' ||
        normalized === '$0.00' ||
        normalized === '0' ||
        normalized === '0.0' ||
        normalized === '0.00'
    );
};

/**
 * OOB getOrderInformation renders PriceComponent when amount is 0 (shows "FREE").
 * Clear amount on the input so no price node is created for zero-total orders.
 */
export const prepareOrderInformationInput = <T extends { amount?: number | null | undefined }>(input: T): T => {
    if (isZeroOrFreeAmount(input.amount)) {
        return { ...input, amount: undefined };
    }
    return input;
};

/** Hide order header amount when price is zero or rendered as FREE. */
export const renderOrderInformationAmount = (amount: React.ReactNode): React.ReactNode => {
    if (amount == null) {
        return null;
    }
    if (isFreeLikeNode(amount)) {
        return null;
    }
    return amount;
};
